import type { TaskDraft } from "@app/ai";
import type { RuleInput } from "@app/core";

const VALID_DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
const VALID_FREQUENCIES = ["daily", "weekly", "monthly", "yearly"] as const;
type DayCode = (typeof VALID_DAYS)[number];
type Frequency = (typeof VALID_FREQUENCIES)[number];

/**
 * Map the recurrence the NL parser produced onto the `RuleInput` the task store
 * expects. Tolerant of the model's looser shape: clamps interval to ≥1, keeps
 * only valid BYDAY codes, and drops an unparseable `until`. Returns null when
 * there's nothing usable (caller then creates a one-shot task).
 */
export function draftRecurrenceToRuleInput(r: TaskDraft["recurrence"] | unknown): RuleInput | null {
  if (!isRecord(r) || !isFrequency(r.freq)) return null;
  const byDay = Array.isArray(r.byDay)
    ? r.byDay.filter((d): d is DayCode => typeof d === "string" && (VALID_DAYS as readonly string[]).includes(d))
    : [];
  const interval = typeof r.interval === "number" && Number.isFinite(r.interval) && r.interval > 0
    ? Math.max(1, Math.floor(r.interval))
    : 1;
  const byMonthDay = typeof r.byMonthDay === "number" && Number.isInteger(r.byMonthDay)
    && r.byMonthDay >= 1 && r.byMonthDay <= 31
    ? r.byMonthDay
    : null;
  const until = typeof r.until === "string" && r.until ? new Date(r.until) : null;
  return {
    freq: r.freq,
    interval,
    ...(byDay.length ? { byDay } : {}),
    ...(byMonthDay ? { byMonthDay } : {}),
    ...(until && !Number.isNaN(until.getTime()) ? { until } : {}),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFrequency(value: unknown): value is Frequency {
  return typeof value === "string" && (VALID_FREQUENCIES as readonly string[]).includes(value);
}
